import { getSupabaseAdmin } from "@/lib/supabase/server";

/**
 * „Letzter Lauf" eines Crons – als EIN Key pro Cron in `site_settings`
 * (Upsert), statt bei jedem Lauf eine audit_log-Zeile zu erzeugen (der
 * Erinnerungs-Cron läuft alle 5 Minuten → das wären ~290 Zeilen/Tag Rauschen).
 * Das Admin-Dashboard liest die Marker in der System-Zeile aus.
 */
export const CRON_RUN_KEY_PREFIX = "cron_last_run:";

/** Generisch: JSON-Wert aus site_settings lesen (Service-Role, best-effort). */
export async function readJsonSetting<T>(key: string): Promise<T | null> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data } = await (getSupabaseAdmin() as any)
      .from("site_settings")
      .select("value")
      .eq("key", key)
      .maybeSingle();
    const raw = (data as { value?: string } | null)?.value;
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/** Generisch: JSON-Wert in site_settings schreiben (Upsert, best-effort). */
export async function writeJsonSetting(key: string, value: unknown): Promise<void> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (getSupabaseAdmin() as any)
      .from("site_settings")
      .upsert({ key, value: JSON.stringify(value) }, { onConflict: "key" });
  } catch (err) {
    console.error(`[settings] writeJsonSetting(${key}) failed:`, err);
  }
}

export type CronRun = { at: string } & Record<string, unknown>;

export async function markCronRun(
  name: string,
  meta: Record<string, unknown> = {},
): Promise<void> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (getSupabaseAdmin() as any).from("site_settings").upsert(
      {
        key: `${CRON_RUN_KEY_PREFIX}${name}`,
        value: JSON.stringify({ at: new Date().toISOString(), ...meta }),
      },
      { onConflict: "key" },
    );
  } catch (err) {
    console.error(`[cron] markCronRun(${name}) failed:`, err);
  }
}

export async function readCronRuns(
  names: string[],
): Promise<Record<string, CronRun | null>> {
  const out: Record<string, CronRun | null> = {};
  for (const n of names) out[n] = null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data } = await (getSupabaseAdmin() as any)
      .from("site_settings")
      .select("key, value")
      .in(
        "key",
        names.map((n) => `${CRON_RUN_KEY_PREFIX}${n}`),
      );
    for (const row of (data ?? []) as { key: string; value: string }[]) {
      const name = row.key.slice(CRON_RUN_KEY_PREFIX.length);
      try {
        const parsed = JSON.parse(row.value) as CronRun;
        if (parsed && typeof parsed.at === "string") out[name] = parsed;
      } catch {
        /* kaputter Wert → wie „kein Lauf" behandeln */
      }
    }
  } catch (err) {
    console.error("[cron] readCronRuns failed:", err);
  }
  return out;
}
